// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: opening a task. On the phone it is a full-screen sheet (top bar,
// big title, status pill, property rows, description, subtasks, time tracking,
// activity and a sticky comment capsule); editors open as bottom sheets. On the
// desktop the same pieces sit in a two-column modal and editors are popovers.
// Every edit is attributed to the current user in ClickUp. Files can be
// dropped on the description or the composer; they upload as attachments.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {canSendToAgent, sendToAgent} from '@app/features/konvo_agents/state/SendToAgent';
import styles from '@app/features/konvo_tasks/components/KonvoTaskModal.module.css';
import {fmtRelative} from '@app/features/konvo_tasks/components/task_sheet/TaskFormat';
import {
	ActivitySection,
	AssigneeOptions,
	AttachmentsSection,
	Composer,
	DescriptionSection,
	DueEditor,
	type EditorHost,
	EditTrigger,
	MentionOptions,
	PriorityOptions,
	PropertyCard,
	StatusOptions,
	StatusRow,
	SubtasksSection,
	TASK_DESCRIPTORS,
	type TaskEditorKind,
	TaskMeta,
	TaskTitle,
	TimeLogForm,
	TimeSection,
} from '@app/features/konvo_tasks/components/task_sheet/TaskParts';
import parts from '@app/features/konvo_tasks/components/task_sheet/TaskParts.module.css';
import {type TaskWorkspace, useTaskWorkspace} from '@app/features/konvo_tasks/components/task_sheet/useTaskWorkspace';
import {
	NoticeButton,
	TaskDetailSkeleton,
	TaskNotice,
} from '@app/features/konvo_tasks/components/workspace/shared/TaskNotice';
import type {KonvoTimeEntry} from '@app/features/konvo_tasks/state/KonvoTasks';
import {SEND_TO_AGENT_DESCRIPTOR, taskAgentContext} from '@app/features/konvo_tasks/utils/TaskAgentContext';
import {GlassBackdrop} from '@app/features/theme/components/GlassBackdrop';
import {BottomSheet} from '@app/features/ui/bottom_sheet/BottomSheet';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {GlassMenu} from '@app/features/ui/glass_menu/GlassMenu';
import {
	ArrowUpIcon,
	CopyIcon,
	DotsThreeIcon,
	LinkIcon,
	PaperclipIcon,
	PauseIcon,
	PlusIcon,
	XIcon,
} from '@app/features/ui/icons/Icons';
import {IconButton} from '@app/features/ui/konvo_controls/KonvoControls';
import type {MenuGroupType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {
	PhoneBackButton,
	PhoneCapsuleButton,
	PhoneGlassCapsule,
	PhoneHeaderBar,
} from '@app/features/ui/phone_header/PhoneHeader';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconChecklist, IconExclamationMark, IconTerminal2} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

const LOAD_FAILED_DESCRIPTOR = msg({
	message: 'Couldn’t load this task',
	comment: 'Task card: title of the card when the task did not load.',
});
const LOAD_FAILED_TEXT_DESCRIPTOR = msg({
	message: 'Konvo can’t read it right now. It may have moved, or ClickUp didn’t answer. It may still open in ClickUp.',
	comment: 'Task card: text of the card when the task did not load.',
});
const LOADING_DESCRIPTOR = msg({
	message: 'Loading the task',
	comment: 'Task card: accessible name of the skeleton while the task loads.',
});
const COPY_ID_DESCRIPTOR = msg({message: 'Copy task ID', comment: 'Task detail menu item.'});
const COPY_LINK_DESCRIPTOR = msg({message: 'Copy link', comment: 'Task detail menu item that copies the ClickUp URL.'});
const LOG_TIME_MANUALLY_DESCRIPTOR = msg({
	message: 'Log time manually',
	comment: 'Task detail menu item that opens the manual time entry form.',
});
const PAUSE_TIMER_DESCRIPTOR = msg({message: 'Pause timer', comment: 'Task detail menu item.'});
const OPEN_PARENT_DESCRIPTOR = msg({message: 'Open parent task', comment: 'Task detail menu item on a subtask.'});
const UPDATED_DESCRIPTOR = msg({
	message: 'Updated {when}',
	comment: 'Task detail footer. {when} is relative, e.g. "2 hr. ago".',
});

interface KonvoTaskModalProps {
	taskId: string;
	initialSection?: 'overview' | 'time';
	/** The ClickUp link the task was opened from (a link in a chat). */
	url?: string;
	onClose: () => void;
}

export const KonvoTaskModal = observer(({taskId, onClose, initialSection = 'overview', url}: KonvoTaskModalProps) => {
	const ws = useTaskWorkspace(taskId);
	// Same switch the modal itself uses to go edge to edge.
	return MobileLayout.enabled ? (
		<TaskPhoneSheet ws={ws} onClose={onClose} initialSection={initialSection} url={url} />
	) : (
		<TaskDesktopModal ws={ws} onClose={onClose} initialSection={initialSection} url={url} />
	);
});

interface LayoutProps {
	ws: TaskWorkspace;
	onClose: () => void;
	initialSection: 'overview' | 'time';
	url?: string;
}

const QUICK_CLICKUP_DESCRIPTOR = msg({
	message: 'ClickUp',
	comment: 'Phone task menu: short label under the Open in ClickUp button on the top row.',
});
const QUICK_AGENT_DESCRIPTOR = msg({
	message: 'Agent',
	comment: 'Phone task menu: short label under the Send to an agent button on the top row.',
});

/** The task ⋯ menu's top row on phones. */
const MORE_QUICK_IDS = ['open', 'copy-link', 'agent'];

interface MoreItem {
	key: string;
	/** A shorter label under its button on the menu's top row. */
	quickLabel?: string;
	icon: React.ReactNode;
	label: string;
	onSelect: () => void;
}

/** Rarely used actions behind ⋯, shared by both layouts. */
function useMoreItems(
	ws: TaskWorkspace,
	onLogTime: () => void,
	onAttach: () => void,
	onClose: () => void,
): Array<MoreItem> {
	const {i18n} = useLingui();
	const task = ws.task;
	const items: Array<MoreItem> = [];
	if (!task) return items;
	if (task.url)
		items.push({
			key: 'open',
			icon: <ClickUpLogo size={20} />,
			label: i18n._(TASK_DESCRIPTORS.openInClickUp),
			quickLabel: i18n._(QUICK_CLICKUP_DESCRIPTOR),
			onSelect: () => void openExternalUrl(task.url!),
		});
	items.push({
		key: 'copy-id',
		icon: <CopyIcon size={20} />,
		label: i18n._(COPY_ID_DESCRIPTOR),
		onSelect: () => void ws.copyId(),
	});
	if (task.url)
		items.push({
			key: 'copy-link',
			icon: <LinkIcon size={20} />,
			label: i18n._(COPY_LINK_DESCRIPTOR),
			onSelect: () => void ws.copyLink(),
		});
	if (canSendToAgent())
		items.push({
			key: 'agent',
			icon: <IconTerminal2 size={20} stroke={1.75} />,
			label: i18n._(SEND_TO_AGENT_DESCRIPTOR),
			quickLabel: i18n._(QUICK_AGENT_DESCRIPTOR),
			onSelect: () => {
				onClose();
				void sendToAgent(taskAgentContext(task));
			},
		});
	items.push({
		key: 'log',
		icon: <PlusIcon size={20} />,
		label: i18n._(LOG_TIME_MANUALLY_DESCRIPTOR),
		onSelect: onLogTime,
	});
	if (ws.runningHere)
		items.push({
			key: 'pause',
			icon: <PauseIcon size={20} />,
			label: i18n._(PAUSE_TIMER_DESCRIPTOR),
			onSelect: ws.pauseTimer,
		});
	items.push({
		key: 'attach',
		icon: <PaperclipIcon size={20} />,
		label: i18n._(TASK_DESCRIPTORS.attach),
		onSelect: onAttach,
	});
	if (task.parent)
		items.push({
			key: 'parent',
			icon: <ArrowUpIcon size={20} />,
			label: i18n._(OPEN_PARENT_DESCRIPTOR),
			onSelect: () => ws.openTask(task.parent!),
		});
	return items;
}

function Crumbs({ws}: {ws: TaskWorkspace}) {
	const task = ws.task;
	const path = [task?.space.name, task?.folder.name, task?.list.name].filter(Boolean) as Array<string>;
	return <>{path.join(' / ')}</>;
}

/** Loading (a skeleton of the card), or the error with a way out to ClickUp (a task Konvo cannot
 *  see may still open there). */
const LoadState = observer(
	({ws, url, onClose, phone}: {ws: TaskWorkspace; url?: string; onClose: () => void; phone: boolean}) => {
		const {i18n} = useLingui();
		if (!ws.loadError) return <TaskDetailSkeleton phone={phone} label={i18n._(LOADING_DESCRIPTOR)} />;
		const link = url ?? `https://app.clickup.com/t/${encodeURIComponent(ws.currentId)}`;
		return (
			<TaskNotice
				place={phone ? 'phone' : 'main'}
				icon={<IconChecklist size={20} stroke={1.7} />}
				badge={<IconExclamationMark size={12} stroke={2.6} />}
				tone="bad"
				title={i18n._(LOAD_FAILED_DESCRIPTOR)}
				text={i18n._(LOAD_FAILED_TEXT_DESCRIPTOR)}
				actions={
					<NoticeButton
						icon={<ClickUpLogo size={14} />}
						onClick={() => {
							onClose();
							void openExternalUrl(link);
						}}
						flx="konvo-tasks.task-modal.load-failed-open-clickup"
					>
						{i18n._(TASK_DESCRIPTORS.openInClickUp)}
					</NoticeButton>
				}
				flx="konvo-tasks.task-modal.load-failed"
			/>
		);
	},
);

const UpdatedLine = observer(({ws}: {ws: TaskWorkspace}) => {
	const {i18n} = useLingui();
	if (ws.task?.date_updated == null) return null;
	return <p className={styles.updated}>{i18n._(UPDATED_DESCRIPTOR, {when: fmtRelative(ws.task.date_updated)})}</p>;
});

/** Scroll the time section into view once when the task was opened on it. */
function useInitialSection(
	ws: TaskWorkspace,
	initialSection: 'overview' | 'time',
	timeRef: React.RefObject<HTMLDivElement | null>,
) {
	const done = useRef(false);
	const ready = ws.task != null;
	useEffect(() => {
		if (done.current || !ready || initialSection !== 'time') return;
		done.current = true;
		requestAnimationFrame(() => timeRef.current?.scrollIntoView({block: 'start'}));
	}, [ready, initialSection, timeRef]);
}

/** Keep a fresh comment in view: scroll to the end when the count grows (not on first load). */
function useFollowComments(ws: TaskWorkspace, scrollRef: React.RefObject<HTMLDivElement | null>) {
	const count = ws.comments.length;
	const loaded = ws.detail != null;
	const previous = useRef<{id: string; count: number} | null>(null);
	useEffect(() => {
		if (!loaded) return;
		const last = previous.current;
		previous.current = {id: ws.currentId, count};
		if (!last || last.id !== ws.currentId || count <= last.count) return;
		const element = scrollRef.current;
		if (element) element.scrollTo({top: element.scrollHeight, behavior: 'smooth'});
	}, [count, loaded, ws.currentId, scrollRef]);
}

// ── Phone: full-screen sheet ─────────────────────────────────────────────

const TaskPhoneSheet = observer(({ws, onClose, initialSection, url}: LayoutProps) => {
	const {i18n} = useLingui();
	const [sheet, setSheet] = useState<TaskEditorKind | null>(null);
	const [scrolled, setScrolled] = useState(false);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const headRef = useRef<HTMLDivElement | null>(null);
	const timeRef = useRef<HTMLDivElement | null>(null);
	const fileRef = useRef<HTMLInputElement | null>(null);
	const task = ws.task;
	const closeSheet = useCallback(() => setSheet(null), []);
	useInitialSection(ws, initialSection, timeRef);
	useFollowComments(ws, scrollRef);
	useEffect(() => {
		// A different task (a subtask, the parent) starts at the top.
		scrollRef.current?.scrollTo({top: 0});
		setScrolled(false);
		setSheet(null);
	}, [ws.currentId]);

	const openLog = useCallback(
		(entry: KonvoTimeEntry | null) => {
			ws.beginLog(entry);
			setSheet('time');
		},
		[ws],
	);
	const host: EditorHost = {mobile: true, openSheet: setSheet, renderEditor: () => null};
	const moreItems = useMoreItems(
		ws,
		() => openLog(null),
		() => fileRef.current?.click(),
		onClose,
	);
	const moreRef = useRef<HTMLButtonElement | null>(null);
	// The glass menu: Open in ClickUp, Copy link and Send to an agent on its top row, the rest below.
	const moreGroups: Array<MenuGroupType> = [
		{
			items: moreItems.map((item) => ({
				id: item.key,
				icon: item.icon,
				label: item.label,
				quickLabel: item.quickLabel,
				onClick: item.onSelect,
			})),
		},
	];
	const onScroll = () => {
		const head = headRef.current;
		const scroller = scrollRef.current;
		if (!head || !scroller) return;
		setScrolled(scroller.scrollTop > head.offsetTop + head.offsetHeight - 8);
	};

	const editorSheet = (kind: TaskEditorKind, title: string, content: React.ReactNode) => (
		<BottomSheet
			isOpen={sheet === kind}
			onClose={closeSheet}
			title={title}
			snapPoints={[0, 1]}
			initialSnap={1}
			fitContent
			pinToBottom
			contentClassName={clsx(parts.surface, styles.sheetContent)}
		>
			{sheet === kind && content}
		</BottomSheet>
	);

	return (
		<Modal.Root
			size="xlarge"
			centered={false}
			onClose={onClose}
			initialFocusRef={scrollRef}
			className={styles.phoneRoot}
			data-flx="konvo-tasks.task-modal.root"
		>
			<Modal.ScreenReaderLabel text={task?.name ?? 'Task'} />
			<div className={clsx(parts.surface, styles.phone)}>
				<GlassBackdrop />
				<PhoneHeaderBar as="header" className={styles.phoneBar} data-flx="konvo-tasks.task-modal.top-row">
					<PhoneBackButton
						kind="down"
						label={i18n._(TASK_DESCRIPTORS.close)}
						onClick={onClose}
						data-flx="konvo-tasks.task-modal.close"
					/>
					<span className={clsx(styles.barText, scrolled && styles.barTitle)}>
						{scrolled ? task?.name : <Crumbs ws={ws} />}
					</span>
					<PhoneGlassCapsule>
						{task?.url && !scrolled && (
							<PhoneCapsuleButton
								label={i18n._(TASK_DESCRIPTORS.openInClickUp)}
								onClick={() => openExternalUrl(task.url!)}
								data-flx="konvo-tasks.task-modal.open-clickup"
							>
								<ClickUpLogo size={20} />
							</PhoneCapsuleButton>
						)}
						<PhoneCapsuleButton
							ref={moreRef}
							label={i18n._(TASK_DESCRIPTORS.more)}
							disabled={!task}
							onClick={() => setSheet('more')}
							aria-haspopup="menu"
							data-flx="konvo-tasks.task-modal.more"
						>
							<DotsThreeIcon size={22} weight="bold" />
						</PhoneCapsuleButton>
					</PhoneGlassCapsule>
				</PhoneHeaderBar>
				<div
					className={styles.phoneScroll}
					ref={scrollRef}
					tabIndex={-1}
					onScroll={onScroll}
					data-flx="konvo-tasks.task-modal.body"
				>
					{!task ? (
						<LoadState ws={ws} url={url} onClose={onClose} phone />
					) : (
						<>
							<div className={styles.phoneHead} ref={headRef}>
								<TaskTitle ws={ws} />
								<TaskMeta ws={ws} />
							</div>
							<div className={styles.phoneStatus}>
								<StatusRow ws={ws} host={host} />
							</div>
							<div className={styles.phoneBlock}>
								<PropertyCard ws={ws} host={host} />
								<DescriptionSection ws={ws} mobile />
								<AttachmentsSection ws={ws} />
								<SubtasksSection ws={ws} />
								<div ref={timeRef} className={styles.anchor}>
									<TimeSection ws={ws} onLog={openLog} />
								</div>
								<ActivitySection ws={ws} />
								<UpdatedLine ws={ws} />
							</div>
						</>
					)}
				</div>
				{task && (
					<div className={styles.phoneDock}>
						<Composer ws={ws} host={host} />
					</div>
				)}
				<input
					ref={fileRef}
					type="file"
					multiple
					hidden
					onChange={(event) => {
						void ws.attachUpload.upload(event.target.files);
						event.target.value = '';
					}}
				/>
			</div>
			{editorSheet('status', i18n._(TASK_DESCRIPTORS.status), <StatusOptions ws={ws} close={closeSheet} />)}
			{editorSheet('assignees', i18n._(TASK_DESCRIPTORS.assignees), <AssigneeOptions ws={ws} />)}
			{editorSheet('due', i18n._(TASK_DESCRIPTORS.due), <DueEditor ws={ws} close={closeSheet} />)}
			{editorSheet('priority', i18n._(TASK_DESCRIPTORS.priority), <PriorityOptions ws={ws} close={closeSheet} />)}
			{editorSheet('mention', i18n._(TASK_DESCRIPTORS.mention), <MentionOptions ws={ws} close={closeSheet} />)}
			{editorSheet(
				'time',
				i18n._(ws.timeDraft.entry ? TASK_DESCRIPTORS.editEntry : TASK_DESCRIPTORS.logTime),
				<TimeLogForm ws={ws} onDone={closeSheet} />,
			)}
			<GlassMenu
				isOpen={sheet === 'more'}
				onClose={closeSheet}
				anchor={sheet === 'more' ? moreRef.current : null}
				quickIds={MORE_QUICK_IDS}
				title={task?.name}
				groups={sheet === 'more' ? moreGroups : []}
				data-flx="konvo-tasks.task-modal.more-menu"
			/>
		</Modal.Root>
	);
});

// ── Desktop: two-column modal ────────────────────────────────────────────

const TaskDesktopModal = observer(({ws, onClose, initialSection, url}: LayoutProps) => {
	const {i18n} = useLingui();
	const [logOpen, setLogOpen] = useState(false);
	const sideRef = useRef<HTMLDivElement | null>(null);
	const deskRef = useRef<HTMLDivElement | null>(null);
	const timeRef = useRef<HTMLDivElement | null>(null);
	const task = ws.task;
	useInitialSection(ws, initialSection, timeRef);
	useFollowComments(ws, sideRef);
	useEffect(() => setLogOpen(false), [ws.currentId]);
	const openLog = useCallback(
		(entry: KonvoTimeEntry | null) => {
			ws.beginLog(entry);
			setLogOpen(true);
			requestAnimationFrame(() => timeRef.current?.scrollIntoView({block: 'nearest', behavior: 'smooth'}));
		},
		[ws],
	);
	const moreItems = useMoreItems(
		ws,
		() => openLog(null),
		() => document.querySelector<HTMLInputElement>('[data-task-description-file]')?.click(),
		onClose,
	);
	const host: EditorHost = {
		mobile: false,
		openSheet: () => {},
		renderEditor: (kind, close) => {
			switch (kind) {
				case 'status':
					return <StatusOptions ws={ws} close={close} />;
				case 'assignees':
					return <AssigneeOptions ws={ws} />;
				case 'due':
					return <DueEditor ws={ws} close={close} />;
				case 'priority':
					return <PriorityOptions ws={ws} close={close} />;
				case 'mention':
					return <MentionOptions ws={ws} close={close} />;
				case 'more':
					return (
						<div className={parts.options} role="menu">
							{moreItems.map((item) => (
								<button
									key={item.key}
									type="button"
									role="menuitem"
									className={parts.option}
									onClick={() => {
										close();
										item.onSelect();
									}}
								>
									<span className={parts.optionLead}>{item.icon}</span>
									<span className={parts.optionLabel}>{item.label}</span>
								</button>
							))}
						</div>
					);
				default:
					return null;
			}
		},
	};

	return (
		<Modal.Root
			size="xlarge"
			centered
			onClose={onClose}
			initialFocusRef={deskRef}
			data-flx="konvo-tasks.task-modal.root"
		>
			<Modal.ScreenReaderLabel text={task?.name ?? 'Task'} data-flx="konvo-tasks.task-modal.sr-label" />
			<div className={clsx(parts.surface, styles.desk)} ref={deskRef} tabIndex={-1}>
				<div className={styles.deskTop} data-flx="konvo-tasks.task-modal.top-row">
					<span className={styles.deskCrumbs} data-flx="konvo-tasks.task-modal.crumbs">
						<ClickUpLogo size={14} />
						<Crumbs ws={ws} />
					</span>
					{task?.url && (
						<IconButton
							label={i18n._(TASK_DESCRIPTORS.openInClickUp)}
							icon={<ClickUpLogo size={18} />}
							className={parts.tonalRound}
							onClick={() => openExternalUrl(task.url!)}
							data-flx="konvo-tasks.task-modal.open-clickup"
						/>
					)}
					{task && (
						<EditTrigger host={host} kind="more">
							<IconButton
								label={i18n._(TASK_DESCRIPTORS.more)}
								icon={<DotsThreeIcon size={20} weight="bold" />}
								className={parts.tonalRound}
								data-flx="konvo-tasks.task-modal.more"
							/>
						</EditTrigger>
					)}
					<IconButton
						label={i18n._(TASK_DESCRIPTORS.close)}
						icon={<XIcon size={18} />}
						className={parts.tonalRound}
						onClick={onClose}
						data-flx="konvo-tasks.task-modal.close"
					/>
				</div>
				{!task ? (
					<LoadState ws={ws} url={url} onClose={onClose} phone={false} />
				) : (
					<div className={styles.deskColumns}>
						<div className={styles.deskMain} data-flx="konvo-tasks.task-modal.body">
							<div className={styles.deskHead}>
								<TaskTitle ws={ws} />
								<TaskMeta ws={ws} />
							</div>
							<StatusRow ws={ws} host={host} />
							<div className={styles.deskStack}>
								<PropertyCard ws={ws} host={host} />
								<DescriptionSection ws={ws} mobile={false} />
								<AttachmentsSection ws={ws} />
								<SubtasksSection ws={ws} />
								<UpdatedLine ws={ws} />
							</div>
						</div>
						<aside className={styles.deskSide}>
							<div className={styles.deskSideScroll} ref={sideRef}>
								<div ref={timeRef} className={styles.anchor}>
									<TimeSection
										ws={ws}
										onLog={openLog}
										logForm={logOpen ? <TimeLogForm ws={ws} inline onDone={() => setLogOpen(false)} /> : undefined}
									/>
								</div>
								<ActivitySection ws={ws} />
							</div>
							<div className={styles.deskComposer}>
								<Composer ws={ws} host={host} />
							</div>
						</aside>
					</div>
				)}
			</div>
		</Modal.Root>
	);
});
